import { ReactNode } from 'react';

import { MinusIcon, TrendingDown, TrendingUp } from 'lucide-react';

import { formatTrimmedDecimal } from '@/helpers/finance';
import { cn } from '@/lib/utils';

import { StatCard } from './stat-card';
import { StatCardRow } from './stat-card-row';

const MARGIN_DISPLAY_DECIMAL_PLACES = 1;

/**
 * Smallest rendered share of the proportion meter (%). Keeps a tiny-but-real
 * segment visible instead of collapsing it to an invisible sliver.
 */
const METER_MIN_VISIBLE_SHARE = 4;

export interface ComparisonResult {
  positiveTotal: number;
  negativeTotal: number;
  /** positiveTotal − negativeTotal. */
  net: number;
  /** net ÷ positive × 100. Null when there is no positive volume to divide by. */
  marginPct: number | null;
  /** Positive share of the |positive| + |negative| volume, 0–100. Null when both are 0. */
  positiveShare: number | null;
}

/** Single place the comparison math lives — the card and any compact chip share it. */
export const computeComparison = (
  positiveTotal: number,
  negativeTotal: number
): ComparisonResult => {
  const net = positiveTotal - negativeTotal;

  // Either side can be negative (e.g. income discount lines) — shares compare
  // volumes, so they use absolute values while displayed amounts keep the sign.
  const volume = Math.abs(positiveTotal) + Math.abs(negativeTotal);

  return {
    positiveTotal,
    negativeTotal,
    net,
    marginPct: positiveTotal !== 0 ? (net / Math.abs(positiveTotal)) * 100 : null,
    positiveShare: volume > 0 ? (Math.abs(positiveTotal) / volume) * 100 : null,
  };
};

/** "+150 USD" / "-70 USD" — the net keeps an explicit sign for positive values. */
export const formatComparisonNet = (
  net: number,
  formatAmount: (amount: number) => string
): string => `${net > 0 ? '+' : ''}${formatAmount(net)}`;

/**
 * Polarity classes for the net value. Direction is never color-alone — the
 * value ships with a trend icon (card) or an explicit label (chips/tooltips).
 * Emerald steps match the meter palette; negative reuses the app destructive.
 */
export const getComparisonPolarityClass = (net: number): string => {
  if (net > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (net < 0) return 'text-destructive';
  return 'text-foreground';
};

export const NetTrendIcon = ({ net, className }: { net: number; className?: string }) => {
  const Icon = net > 0 ? TrendingUp : net < 0 ? TrendingDown : MinusIcon;
  return <Icon size={16} className={cn('shrink-0', className)} aria-hidden />;
};

export interface ComparisonSide {
  /** Direct label next to the meter dot, e.g. "Total income". */
  label: string;
  amount: number;
}

/**
 * Positive vs negative proportion meter: one track, two direct-labeled
 * segments with a 2px card-surface gap. Palette (validated with the dataviz
 * CVD/contrast checks on both surfaces): positive emerald-500/600, negative
 * amber-500/600 — the labels carry identity, the hue is reinforcement only,
 * so keep the labels when reusing this.
 */
export const ComparisonMeter = ({
  comparison,
  positive,
  negative,
  formatAmount,
}: {
  comparison: ComparisonResult;
  positive: ComparisonSide;
  negative: ComparisonSide;
  formatAmount: (amount: number) => string;
}) => {
  const { positiveTotal, negativeTotal, positiveShare } = comparison;

  const clampedPositiveShare =
    positiveShare === null
      ? null
      : Math.min(
          Math.max(positiveShare, positiveTotal !== 0 ? METER_MIN_VISIBLE_SHARE : 0),
          negativeTotal !== 0 ? 100 - METER_MIN_VISIBLE_SHARE : 100
        );

  return (
    <div className="mt-auto flex flex-col gap-1.5">
      <div className="flex h-2 gap-0.5 overflow-hidden rounded-full" aria-hidden>
        {clampedPositiveShare === null ? (
          <div className="flex-1 bg-border" />
        ) : (
          <>
            {clampedPositiveShare > 0 && (
              <div
                className="rounded-full bg-emerald-500 dark:bg-emerald-600"
                style={{ width: `${clampedPositiveShare}%` }}
              />
            )}
            {clampedPositiveShare < 100 && (
              <div
                className="flex-1 rounded-full bg-amber-500 dark:bg-amber-600"
                style={{ width: `${100 - clampedPositiveShare}%` }}
              />
            )}
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 dark:bg-emerald-600" />
          <span className="truncate text-muted-foreground">{positive.label}</span>
          <span className="whitespace-nowrap font-medium tabular-nums">
            {formatAmount(positive.amount)}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500 dark:bg-amber-600" />
          <span className="truncate text-muted-foreground">{negative.label}</span>
          <span className="whitespace-nowrap font-medium tabular-nums">
            {formatAmount(negative.amount)}
          </span>
        </span>
      </div>
    </div>
  );
};

export interface ComparisonStatCardProps {
  icon: ReactNode;
  title: ReactNode;
  infoTooltip?: ReactNode;
  /** The side that increases the net (e.g. income, revenue, inflow). */
  positive: ComparisonSide;
  /** The side that decreases the net (e.g. cost, spend, outflow). */
  negative: ComparisonSide;
  /** Label of the net row, e.g. "Net (income − cost)". */
  netLabel: string;
  /** Label of the margin row, e.g. "Margin (net ÷ income)". */
  marginLabel: string;
  /** Rendered instead of the margin when there is no positive volume. */
  noValueLabel: string;
  /** Formats an amount for display, e.g. `250 → "250 USD"`. */
  formatAmount: (amount: number) => string;
}

/**
 * A StatCard comparing a positive and a negative amount: the headline is the
 * net difference with polarity color coding + trend icon, followed by the
 * margin and a proportion meter of the two volumes.
 */
export const ComparisonStatCard = ({
  icon,
  title,
  infoTooltip,
  positive,
  negative,
  netLabel,
  marginLabel,
  noValueLabel,
  formatAmount,
}: ComparisonStatCardProps) => {
  const comparison = computeComparison(positive.amount, negative.amount);

  return (
    <StatCard icon={icon} title={title} infoTooltip={infoTooltip}>
      <StatCardRow
        label={netLabel}
        value={
          <span
            className={cn(
              'flex items-center gap-1.5 whitespace-nowrap text-base font-semibold tabular-nums',
              getComparisonPolarityClass(comparison.net)
            )}
          >
            <NetTrendIcon net={comparison.net} />
            {formatComparisonNet(comparison.net, formatAmount)}
          </span>
        }
      />
      <StatCardRow
        label={marginLabel}
        value={
          comparison.marginPct === null ? (
            <span className="text-muted-foreground">{noValueLabel}</span>
          ) : (
            <span className="tabular-nums">
              {formatTrimmedDecimal(comparison.marginPct, MARGIN_DISPLAY_DECIMAL_PLACES)}%
            </span>
          )
        }
      />
      <ComparisonMeter
        comparison={comparison}
        positive={positive}
        negative={negative}
        formatAmount={formatAmount}
      />
    </StatCard>
  );
};
